import { z } from "zod/v4";

import { assertDescriptions } from "../descriptions/types";
import {
  CreateVariationDtoDescriptions,
  VariationDtoDescriptions,
} from "../descriptions/variation-dto.describe";

import { CartTargetingSchema } from "./cart-targeting";
import { ComponentDtoSchema } from "./components";
import { BlockTypeList, CheckoutBlockDtoSchema } from "./checkout-blocks";
import {
  DiscountApplicationMethodList,
  DiscountApplicationTypeList,
  OfferDtoSchema,
} from "./offers";
import { OnsiteEditDtoSchema } from "./onsite-edits";
import {
  CreateOnsiteInjectionDtoSchema,
  OnsiteInjectionDtoSchema,
} from "./onsite-injections";
import { RedirectDtoSchema } from "./redirects";
import {
  CreateShippingRateGroupDtoSchema,
  ShippingRateGroupDtoSchema,
} from "./shipping-rate-groups";
import { UserInterfaceDtoSchema } from "./user-interfaces";

export const PriceChangeUnitList = ["percent", "dollar"] as const;
export type PriceChangeUnit = (typeof PriceChangeUnitList)[number];

export const PriceChangeRoundingList = [
  "none",
  "nearest",
  "up",
  "down",
] as const;

export type PriceChangeRounding = (typeof PriceChangeRoundingList)[number];
export const PriceDirectionList = ["increase", "decrease"] as const;
export type PriceDirection = (typeof PriceDirectionList)[number];

// Canonical price-transform shape. Schema-first so callers (e.g. the AI price
// tools) can reuse it directly instead of re-declaring a parallel zod mirror.
export const PriceTransformSchema = z.object({
  change: z.number(),
  changeUnit: z.enum(PriceChangeUnitList),
  roundingType: z.enum(PriceChangeRoundingList).optional(),
  roundingAmount: z.number().optional(),
});

export type PriceTransform = z.infer<typeof PriceTransformSchema>;

export const VariationDtoSchema = z.object({
  id: z.uuid().describe(VariationDtoDescriptions.id),
  experienceId: z.uuid().describe(VariationDtoDescriptions.experienceId),
  name: z.string().describe(VariationDtoDescriptions.name),
  percentage: z.number().describe(VariationDtoDescriptions.percentage),
  isControl: z
    .boolean()
    .default(false)
    .describe(VariationDtoDescriptions.isControl),
  order: z.number().int().describe(VariationDtoDescriptions.order),
  priceChange: z
    .string()
    .nullish()
    .describe(VariationDtoDescriptions.priceChange),
  priceChangeUnit: z
    .enum(PriceChangeUnitList)
    .nullish()
    .describe(VariationDtoDescriptions.priceChangeUnit),
  priceRoundingType: z
    .enum(PriceChangeRoundingList)
    .nullish()
    .describe(VariationDtoDescriptions.priceRoundingType),
  priceRoundingAmount: z
    .string()
    .nullish()
    .describe(VariationDtoDescriptions.priceRoundingAmount),
  onsiteEdits: OnsiteEditDtoSchema.array()
    .optional()
    .default([])
    .describe(VariationDtoDescriptions.onsiteEdits),
  onsiteInjections: OnsiteInjectionDtoSchema.nullish().describe(
    VariationDtoDescriptions.onsiteInjections
  ),
  offer: OfferDtoSchema.nullish().describe(VariationDtoDescriptions.offer),
  offerId: z.uuid().nullish().describe(VariationDtoDescriptions.offerId),
  shippingRateGroups: z
    .optional(ShippingRateGroupDtoSchema.array().default([]))
    .describe(VariationDtoDescriptions.shippingRateGroups),
  excludeProducts: z
    .boolean()
    .default(false)
    .optional()
    .describe(VariationDtoDescriptions.excludeProducts),
  includeProducts: z
    .boolean()
    .default(false)
    .optional()
    .describe(VariationDtoDescriptions.includeProducts),
  testExperienceId: z
    .string()
    .nullish()
    .describe(VariationDtoDescriptions.testExperienceId),
  functionSyncError: z
    .boolean()
    .default(false)
    .describe(VariationDtoDescriptions.functionSyncError),
  redirects: z
    .optional(RedirectDtoSchema.array().default([]))
    .describe(VariationDtoDescriptions.redirects),
  userInterfaces: z
    .optional(UserInterfaceDtoSchema.array().default([]))
    .describe(VariationDtoDescriptions.userInterfaces),
  checkoutBlocks: z
    .optional(CheckoutBlockDtoSchema.array().default([]))
    .describe(VariationDtoDescriptions.checkoutBlocks),
  cartTargeting: z
    .optional(CartTargetingSchema.array().default([]))
    .describe(VariationDtoDescriptions.cartTargeting),
  components: z
    .optional(ComponentDtoSchema.array().default([]))
    .describe(VariationDtoDescriptions.components),
});

export type VariationDto = z.infer<typeof VariationDtoSchema>;
export type VariationIdNameOrder = Pick<VariationDto, "id" | "name" | "order">;

export const ExperienceListVariationDtoSchema = VariationDtoSchema.pick({
  id: true,
  experienceId: true,
  name: true,
  percentage: true,
  isControl: true,
  order: true,
  functionSyncError: true,
  testExperienceId: true,
  offerId: true,
  redirects: true,
}).extend({
  // Slim modification discriminators for client-side sub-filtering on the
  // experiences list (the full `offer`/`checkoutBlocks` relations are
  // intentionally omitted to keep the paginated payload lean):
  // - checkoutBlockTypes drives the checkout table's upsell/trustBadge/timer filters
  // - offerDiscountApplication{Type,Method} drive the merchandising/pricing/
  //   shipping offer sub-filters (e.g. freeShipping vs volumeDiscount).
  checkoutBlockTypes: z.array(z.enum(BlockTypeList)).optional(),
  offerDiscountApplicationType: z
    .enum(DiscountApplicationTypeList)
    .nullish()
    .optional(),
  offerDiscountApplicationMethod: z
    .enum(DiscountApplicationMethodList)
    .nullish()
    .optional(),
});

export const ExperienceListWithOffersVariationDtoSchema =
  VariationDtoSchema.pick({
    id: true,
    experienceId: true,
    name: true,
    percentage: true,
    isControl: true,
    order: true,
    functionSyncError: true,
    testExperienceId: true,
    offerId: true,
    redirects: true,
    offer: true,
    userInterfaces: true,
  });

export type ExperienceListVariationDto = z.infer<
  typeof ExperienceListVariationDtoSchema
>;

export const PatchVariationDtoSchema = z
  .object({
    experienceId: z.uuid(),
    name: z.string(),
    percentage: z.number(),
    isControl: z.boolean().default(false),
    order: z.number().int(),
    priceChange: z.string().nullish(),
    priceChangeUnit: z.enum(PriceChangeUnitList).nullish(),
    priceRoundingType: z.enum(PriceChangeRoundingList).nullish(),
    priceRoundingAmount: z.string().nullish(),
    onsiteEdits: OnsiteEditDtoSchema.array().optional().default([]),
    onsiteInjections: CreateOnsiteInjectionDtoSchema.nullish(),
    offer: OfferDtoSchema.nullish(),
    shippingRateGroups: z.optional(
      ShippingRateGroupDtoSchema.array().default([])
    ),
    excludeProducts: z.boolean().optional().default(false),
    includeProducts: z.boolean().optional().default(false),
    testExperienceId: z.string().nullish(),
    functionSyncError: z.boolean().default(false),
    redirects: z.optional(RedirectDtoSchema.array().default([])),
    checkoutBlocks: z.optional(CheckoutBlockDtoSchema.array().default([])),
    userInterfaces: z.optional(UserInterfaceDtoSchema.array().default([])),
  })
  .partial()
  .extend({
    id: z.uuid(),
  });

export type PatchVariationDto = z.infer<typeof PatchVariationDtoSchema>;

export const CreateVariationDtoSchema = VariationDtoSchema.omit({
  onsiteEdits: true,
  onsiteInjections: true,
  functionSyncError: true,
}).extend({
  experienceId: z
    .uuid()
    .optional()
    .describe(CreateVariationDtoDescriptions.experienceId),
  shippingRateGroups: z
    .optional(CreateShippingRateGroupDtoSchema.array().default([]))
    .describe(CreateVariationDtoDescriptions.shippingRateGroups),
  redirects: z
    .optional(RedirectDtoSchema.array().default([]))
    .describe(CreateVariationDtoDescriptions.redirects),
  name: z
    .string()
    .trim()
    .min(1, "Variation name cannot be empty")
    .max(255, "Variation name cannot exceed 255 characters")
    .describe(CreateVariationDtoDescriptions.name),
  percentage: z
    .number()
    .min(0, "Percentage must be at least 0")
    .max(100, "Percentage cannot exceed 100")
    .describe(CreateVariationDtoDescriptions.percentage),
  isControl: VariationDtoSchema.shape.isControl.describe(
    CreateVariationDtoDescriptions.isControl
  ),
  order: z
    .number()
    .int()
    .nonnegative("Order must be a non-negative integer")
    .describe(CreateVariationDtoDescriptions.order),
  offer: VariationDtoSchema.shape.offer.describe(
    CreateVariationDtoDescriptions.offer
  ),
});

export type CreateVariationDto = z.infer<typeof CreateVariationDtoSchema>;

export const RedactedVariationSchema = VariationDtoSchema.omit({
  onsiteEdits: true,
  onsiteInjections: true,
  offer: true,
  shippingRateGroups: true,
  redirects: true,
  userInterfaces: true,
  checkoutBlocks: true,
});

export type RedactedVariationDto = z.infer<typeof RedactedVariationSchema>;

// Compile-time check: description object must cover all schema keys
assertDescriptions<typeof VariationDtoDescriptions, VariationDto>();
